// Fiche contact : entreprise, personne, ville, type, horaires, jours, couleur. Suppression réversible.
import { COLORS, ValidationError, label, type Contact } from '../core/contact';
import { hm, parseTime } from '../core/time';
import type { Store } from '../data/store';
import { el, esc } from './dom';
import { icon } from './icons';
import { showToast } from './toast';
import { mountZonePicker, type ZonePicker } from './zonePicker';

const DAY_LABELS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

let dialog: HTMLDialogElement | null = null;

function ensureDialog(): HTMLDialogElement {
  if (dialog && document.body.contains(dialog)) return dialog;
  dialog = document.createElement('dialog');
  dialog.id = 'editor';
  dialog.setAttribute('aria-labelledby', 'editorTitle');
  dialog.innerHTML = `<form method="dialog" novalidate>
    <header class="dlg-head"><div><h2 id="editorTitle">Nouveau contact</h2><p>Une entreprise, une personne, une ville, ses horaires.</p></div>
      <button class="icon-btn" type="button" data-close aria-label="Fermer">${icon('close')}</button></header>
    <div class="dlg-body">
      <div class="two">
        <div class="field"><label for="c-org">Entreprise</label><input id="c-org" maxlength="100" autocomplete="organization" placeholder="ARTURA"></div>
        <div class="field"><label for="c-name">Personne</label><input id="c-name" maxlength="80" autocomplete="off" placeholder="Prénom Nom"></div>
      </div>
      <div class="field"><label for="c-zone">Ville</label><div data-zone></div></div>
      <div class="field"><label>Type de contact</label><div class="seg" role="group" aria-label="Type de contact" data-role>
        <button type="button" data-role-value="client" aria-pressed="true">Client</button>
        <button type="button" data-role-value="supplier" aria-pressed="false">Fournisseur</button></div>
        <small>Un client, je m’adapte à lui. Un fournisseur, je lui mets la pression.</small></div>
      <div class="two">
        <div class="field"><label for="c-start">Début de journée <span class="muted">· heure locale</span></label><input id="c-start" type="time" step="60" required value="09:00"></div>
        <div class="field"><label for="c-end">Fin de journée</label><input id="c-end" type="time" step="60" required value="18:00"></div>
      </div>
      <div class="field"><label>Jours travaillés</label><div class="seg" role="group" aria-label="Jours travaillés" data-days></div>
        <small>Pour un horaire de nuit, cochez le jour où la journée commence.</small></div>
      <div class="field"><label>Couleur</label><div class="swatches" role="group" aria-label="Couleur" data-colors></div></div>
      <p class="error" role="alert" data-error></p>
    </div>
    <footer class="dlg-foot">
      <button class="btn ghost danger left" type="button" data-delete hidden>${icon('trash')}Supprimer</button>
      <button class="btn" type="button" data-close>Annuler</button>
      <button class="btn primary" type="submit" data-save>Enregistrer</button>
    </footer></form>`;
  document.body.append(dialog);
  return dialog;
}

export function openEditor(store: Store, contact: Contact | null): void {
  const d = ensureDialog();
  const form = el<HTMLFormElement>(d, 'form');
  const errorEl = el(d, '[data-error]');
  const saveBtn = el<HTMLButtonElement>(d, '[data-save]');
  const deleteBtn = el<HTMLButtonElement>(d, '[data-delete]');
  let role = contact?.role ?? 'client';
  let days = contact ? [...contact.days] : [1, 2, 3, 4, 5];
  let color = contact?.color ?? COLORS[store.active.length % COLORS.length]!;

  el(d, '#editorTitle').textContent = contact ? 'Modifier le contact' : 'Nouveau contact';
  el<HTMLInputElement>(d, '#c-org').value = contact?.org ?? '';
  el<HTMLInputElement>(d, '#c-name').value = contact?.name ?? '';
  el<HTMLInputElement>(d, '#c-start').value = hm(contact?.start ?? 540);
  el<HTMLInputElement>(d, '#c-end').value = hm(contact?.end ?? 1080);
  errorEl.textContent = '';
  deleteBtn.hidden = !contact;
  saveBtn.disabled = false;
  saveBtn.textContent = 'Enregistrer';

  const zone: ZonePicker = mountZonePicker(el(d, '[data-zone]'), {
    id: 'c-zone',
    initial: contact ? { tz: contact.tz, place: contact.place, country: contact.country } : null,
    onPick: () => el<HTMLInputElement>(d, '#c-start').focus(),
  });

  const paintRole = () => d.querySelectorAll<HTMLElement>('[data-role-value]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.roleValue === role)));
  const paintDays = () => { el(d, '[data-days]').innerHTML = DAY_ORDER.map((n) => `<button type="button" data-day="${n}" aria-pressed="${days.includes(n)}">${DAY_LABELS[n]}</button>`).join(''); };
  const paintColors = () => { el(d, '[data-colors]').innerHTML = COLORS.map((c, i) => `<button type="button" class="swatch" data-color="${c}" style="background:${c}" aria-pressed="${c === color}" aria-label="Couleur ${i + 1}"></button>`).join(''); };
  paintRole(); paintDays(); paintColors();

  form.onclick = (e) => {
    const t = e.target as HTMLElement;
    const r = t.closest<HTMLElement>('[data-role-value]');
    if (r) { role = r.dataset.roleValue as 'client' | 'supplier'; paintRole(); return; }
    const dayBtn = t.closest<HTMLElement>('[data-day]');
    if (dayBtn) { const n = Number(dayBtn.dataset.day); days = days.includes(n) ? days.filter((x) => x !== n) : [...days, n]; paintDays(); el<HTMLElement>(d, `[data-day="${n}"]`).focus(); return; }
    const sw = t.closest<HTMLElement>('[data-color]');
    if (sw) { color = sw.dataset.color!; paintColors(); el<HTMLElement>(d, `[data-color="${color}"]`).focus(); return; }
    if (t.closest('[data-close]')) { d.close(); return; }
    if (t.closest('[data-delete]') && contact) { void remove(contact); }
  };

  form.onsubmit = async (e) => {
    e.preventDefault();
    errorEl.textContent = '';
    const z = zone.value;
    if (!z) { errorEl.textContent = 'Choisissez une ville dans la liste.'; zone.focus(); return; }
    const input = {
      org: el<HTMLInputElement>(d, '#c-org').value,
      name: el<HTMLInputElement>(d, '#c-name').value,
      tz: z.tz, place: z.place, country: z.country,
      role,
      start: parseTime(el<HTMLInputElement>(d, '#c-start').value),
      end: parseTime(el<HTMLInputElement>(d, '#c-end').value),
      days, color,
      favorite: contact?.favorite ?? false,
    };
    saveBtn.disabled = true;
    saveBtn.textContent = 'Enregistrement…';
    try {
      const saved = await store.saveContact(input, contact?.id);
      d.close();
      showToast({ id: 0, kind: 'info', text: contact ? `${label(saved)} mis à jour.` : `${label(saved)} ajouté.` });
    } catch (err) {
      errorEl.textContent = err instanceof ValidationError ? err.message : (err instanceof Error ? err.message : 'Enregistrement impossible.');
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Enregistrer';
    }
  };

  async function remove(c: Contact): Promise<void> {
    if (!confirm(`Supprimer ${label(c)} ? Vous pourrez annuler pendant 20 secondes, puis le retrouver dans la corbeille pendant 30 jours.`)) return;
    deleteBtn.disabled = true;
    try { await store.deleteContact(c.id); d.close(); }
    catch (err) { errorEl.textContent = err instanceof Error ? err.message : 'Suppression impossible.'; }
    finally { deleteBtn.disabled = false; }
  }

  d.showModal();
  el<HTMLInputElement>(d, '#c-org').focus();
}
